// A tiny, safe (no eval) recursive-descent evaluator for the +,-,*,/,^ arithmetic that can appear on either
// side of a Tier formula's comparison, once metric tokens have been resolved to numbers.
const parseExpr = (flatTokens) => {
  let pos = 0;
  const peek = () => flatTokens[pos];
  const consume = () => flatTokens[pos++];

  const parsePrimary = () => {
    const tok = consume();
    if (tok === '(') {
      const value = parseAddSub();
      consume(); // ')'
      return value;
    }
    if (tok === '-') return -parsePrimary();
    if (tok === '+') return parsePrimary();
    return Number(tok);
  };

  const parsePow = () => {
    let value = parsePrimary();
    while (peek() === '^') {
      consume();
      value = Math.pow(value, parsePrimary());
    }
    return value;
  };

  const parseMulDiv = () => {
    let value = parsePow();
    while (peek() === '*' || peek() === '/') {
      const op = consume();
      const rhs = parsePow();
      value = op === '*' ? value * rhs : value / rhs;
    }
    return value;
  };

  const parseAddSub = () => {
    let value = parseMulDiv();
    while (peek() === '+' || peek() === '-') {
      const op = consume();
      const rhs = parseMulDiv();
      value = op === '+' ? value + rhs : value - rhs;
    }
    return value;
  };

  const result = parseAddSub();
  if (pos !== flatTokens.length || Number.isNaN(result)) throw new Error('Unable to evaluate expression');
  return result;
};

const evaluateSide = (sideTokens, codeToValue) => {
  if (sideTokens.length === 0) throw new Error('Empty side');
  const flatTokens = sideTokens.map((token) => {
    if (token.type !== 'variable') return token.value;
    // A var that's undefined or unparseable (ex: missing from tierVars) is treated as 0
    const value = Number(codeToValue[token.code]);
    return Number.isNaN(value) ? 0 : value;
  });
  return parseExpr(flatTokens);
};

const COMPARE = {
  '=': (left, right) => left === right,
  '>': (left, right) => left > right,
  '<': (left, right) => left < right,
  '>=': (left, right) => left >= right,
  '<=': (left, right) => left <= right,
};

// Finds the single comparison operator (=, >, <, >=, <=) at paren-depth 0, respecting parens. Returns null if
// there's no top-level comparison, or if AND/OR combine multiple conditions (too ambiguous to summarize).
const findTopLevelComparisonIndex = (tokens) => {
  let depth = 0;
  let comparisonIndex = null;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.type === 'text' && token.value === '(') depth++;
    else if (token.type === 'text' && token.value === ')') depth--;
    else if (depth === 0 && token.type === 'operator') {
      if (token.value === 'AND' || token.value === 'OR') return null;
      if (comparisonIndex == null) comparisonIndex = i;
    }
  }
  return comparisonIndex;
};

// Summarizes a single-comparison Tier formula (ex: "[A] + [B] >= 130000") as its evaluated left/right values and
// whether the comparison was met, using the values in tierVars. Returns null for anything more complex
// (ex: multiple AND/OR conditions), or if a value can't be evaluated.
export const summarizeFormulaComparison = (tokens, codeToValue) => {
  const comparisonIndex = findTopLevelComparisonIndex(tokens);
  if (comparisonIndex == null) return null;

  try {
    const leftValue = evaluateSide(tokens.slice(0, comparisonIndex), codeToValue);
    const operator = tokens[comparisonIndex].value;
    const rightValue = evaluateSide(tokens.slice(comparisonIndex + 1), codeToValue);
    return { leftValue, operator, rightValue, met: COMPARE[operator](leftValue, rightValue) };
  } catch {
    return null;
  }
};
